import { useQuery, type UseQueryOptions, type UseQueryResult } from '@tanstack/react-query';
import { useQueryPayloadWithDeps } from '@shared/hooks';
import { postDropoffQuery, postDropoffPatternsQuery } from '../apis';

export const useDropoffQuery = <
  TQueryFnData = Awaited<ReturnType<typeof postDropoffQuery>>,
  TError = unknown,
  TData = TQueryFnData,
>(
  payload: Parameters<typeof postDropoffQuery>[0],
  options: Omit<
    UseQueryOptions<TQueryFnData, TError, TData>,
    'queryKey' | 'queryFn' | 'initialData'
  > = {},
): UseQueryResult<TData, TError> => {
  const { depKeys, getEnabledStatus, getLoadingStatus, getFetchingStatus } =
    useQueryPayloadWithDeps(payload);
  const enabled = (options.enabled ?? true) && payload.pattern.length > 1;
  const query = useQuery<TQueryFnData, TError, TData, any>({
    keepPreviousData: enabled,
    ...options,
    queryKey: ['/gm/dropoff', payload, depKeys] as const,
    queryFn: () => postDropoffQuery(payload),
    enabled: getEnabledStatus(enabled),
  });

  return {
    ...query,
    isLoading: getLoadingStatus(query.isLoading),
    isFetching: getFetchingStatus(query.isFetching),
  } as typeof query;
};

export const useDropoffPatternsQuery = <
  TQueryFnData = Awaited<ReturnType<typeof postDropoffPatternsQuery>>,
  TError = unknown,
  TData = TQueryFnData,
>(
  payload: Parameters<typeof postDropoffPatternsQuery>[0],
  options: Omit<
    UseQueryOptions<TQueryFnData, TError, TData>,
    'queryKey' | 'queryFn' | 'initialData'
  > = {},
): UseQueryResult<TData, TError> => {
  const { depKeys, getEnabledStatus, getLoadingStatus, getFetchingStatus } =
    useQueryPayloadWithDeps(payload);
  const enabled = (options.enabled ?? true) && payload.pattern.length > 1;
  const query = useQuery<TQueryFnData, TError, TData, any>({
    keepPreviousData: enabled,
    ...options,
    queryKey: ['/gm/dropoff/patterns', payload, depKeys] as const,
    queryFn: () => postDropoffPatternsQuery(payload),
    enabled: getEnabledStatus(enabled),
  });

  return {
    ...query,
    isLoading: getLoadingStatus(query.isLoading),
    isFetching: getFetchingStatus(query.isFetching),
  } as typeof query;
};
